import { z } from "zod";
import { SCRAPE_PROXY_TIER, ScrapeProxyTierSchema } from "../enums/scrape";
import {
  BackendOperationsConfigSchema,
  CrawlReadConfigSchema,
  DeadLetterMonitorConfigSchema,
  IssueExportConfigSchema,
} from "./backend";
import { EvalBatchConfigSchema, EvalJudgeConfigSchema } from "./evals";
import { DemoFleetConfigSchema } from "./fleet";
import {
  SocialAnalyseAttemptConfigSchema,
  SocialAnalyseConfigSchema,
  SocialExtractConfigSchema,
  SocialReadConfigSchema,
} from "./social";
import {
  YeetAttemptConfigSchema,
  YeetCanonicalizeConfigSchema,
  YeetConfigSchema,
  YeetDispatcherConfigSchema,
  YeetScoringConfigSchema,
} from "./yeet";

/** Application settings supplied by AWS AppConfig. The backend pins resolved
 * settings onto queue messages and operation responses. Defaults are the
 * schema baseline, not a fallback for unavailable remote configuration. */

const ViewportSchema = z
  .object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  })
  .strict();

export const FetchConfigSchema = z
  .object({
    maxAttempts: z.number().int().positive().default(4),
    retryBackoffMs: z.number().int().positive().default(1_500),
    proxyTier: ScrapeProxyTierSchema.default(SCRAPE_PROXY_TIER.STEALTH),
    // Proxy geography is NOT here: it is per-website versioned config
    // (website_configs.country_code) carried on the fetch message itself.
    renderWaitMs: z.number().int().min(0).default(2_000),
    // The source and rendered captures are separate proxy requests, so a
    // bot-manager can hand one of them a challenge shell. A source capture
    // smaller than this fraction of the rendered one is refetched once and the
    // larger delivery kept — a genuine pre-JS shell survives both fetches and
    // still wins. 0 disables the refetch.
    sourceShellRatio: z.number().min(0).max(1).default(0.4),
    originFileMaxChars: z.number().int().positive().default(100_000),
    // ScrapingBee screenshot viewports; both shots are required capture
    // evidence and the only visual input the analysis agent gets.
    desktopViewport: ViewportSchema.default({ width: 1_280, height: 720 }),
    mobileViewport: ViewportSchema.default({ width: 390, height: 844 }),
    originClaimPollMs: z.number().int().positive().default(500),
    originClaimWaitMs: z.number().int().positive().default(120_000),
    // Concurrent ScrapingBee renders the fetch worker services (RabbitMQ
    // prefetch / channel QoS). Backend-resolved and pinned onto every fetch
    // message like the render params; the worker applies it from the message
    // and re-applies live on change, so the backend remains the tuning resolver.
    // Must track the sandbox fleet's render demand (yeet.prefetch x
    // yeet replicas), or the render queue backs up into stale rows while blocked
    // pages burn their whole attempt budget waiting.
    prefetch: z.number().int().positive().default(15),
  })
  .strict();
export type FetchConfig = z.infer<typeof FetchConfigSchema>;

export const TovMapConfigSchema = z
  .object({
    model: z.string().default("openai/gpt-4o-mini"),
    concurrency: z.number().int().positive().default(4),
    maxRetries: z.number().int().min(0).default(2),
  })
  .strict();
export type TovMapConfig = z.infer<typeof TovMapConfigSchema>;

export const TovVectorizeConfigSchema = z
  .object({
    maxEmbeddingsPerCall: z.number().int().min(1).max(100).default(100),
    maxParallelCalls: z.number().int().positive().default(4),
    maxRetries: z.number().int().min(0).default(2),
    timeoutMs: z.number().int().positive().default(30_000),
    maxChars: z.number().int().positive().default(1_800),
    // Floor on a merged chunk before it is force-split (chunker.ts).
    minChars: z.number().int().positive().default(500),
  })
  .strict();
export type TovVectorizeConfig = z.infer<typeof TovVectorizeConfigSchema>;

/** Namespace -> schema registry; the resolver and its key type derive from this. */
export const CONFIG_SCHEMAS = {
  demoFleet: DemoFleetConfigSchema,
  yeet: YeetConfigSchema,
  yeetAttempt: YeetAttemptConfigSchema,
  yeetDispatcher: YeetDispatcherConfigSchema,
  yeetScoring: YeetScoringConfigSchema,
  yeetCanonicalize: YeetCanonicalizeConfigSchema,
  fetch: FetchConfigSchema,
  tovMap: TovMapConfigSchema,
  tovVectorize: TovVectorizeConfigSchema,
  crawlRead: CrawlReadConfigSchema,
  issueExport: IssueExportConfigSchema,
  deadLetterMonitor: DeadLetterMonitorConfigSchema,
  backendOperations: BackendOperationsConfigSchema,
  socialExtract: SocialExtractConfigSchema,
  socialAnalyse: SocialAnalyseConfigSchema,
  socialAnalyseAttempt: SocialAnalyseAttemptConfigSchema,
  socialRead: SocialReadConfigSchema,
  evalJudge: EvalJudgeConfigSchema,
  evalBatch: EvalBatchConfigSchema,
} as const;

export type ConfigNamespace = keyof typeof CONFIG_SCHEMAS;
export const ConfigSnapshotSchema = z
  .object({
    demoFleet: CONFIG_SCHEMAS.demoFleet.prefault({}),
    yeet: CONFIG_SCHEMAS.yeet.prefault({}),
    yeetAttempt: CONFIG_SCHEMAS.yeetAttempt.prefault({}),
    yeetDispatcher: CONFIG_SCHEMAS.yeetDispatcher.prefault({}),
    yeetScoring: CONFIG_SCHEMAS.yeetScoring.prefault({}),
    yeetCanonicalize: CONFIG_SCHEMAS.yeetCanonicalize.prefault({}),
    fetch: CONFIG_SCHEMAS.fetch.prefault({}),
    tovMap: CONFIG_SCHEMAS.tovMap.prefault({}),
    tovVectorize: CONFIG_SCHEMAS.tovVectorize.prefault({}),
    crawlRead: CONFIG_SCHEMAS.crawlRead.prefault({}),
    issueExport: CONFIG_SCHEMAS.issueExport.prefault({}),
    deadLetterMonitor: CONFIG_SCHEMAS.deadLetterMonitor.prefault({}),
    backendOperations: CONFIG_SCHEMAS.backendOperations.prefault({}),
    socialExtract: CONFIG_SCHEMAS.socialExtract.prefault({}),
    socialAnalyse: CONFIG_SCHEMAS.socialAnalyse.prefault({}),
    socialAnalyseAttempt: CONFIG_SCHEMAS.socialAnalyseAttempt.prefault({}),
    socialRead: CONFIG_SCHEMAS.socialRead.prefault({}),
    evalJudge: CONFIG_SCHEMAS.evalJudge.prefault({}),
    evalBatch: CONFIG_SCHEMAS.evalBatch.prefault({}),
  })
  .strict();
export const CONFIG_NAMESPACES = ConfigSnapshotSchema.keyof().options;

export const configFlagKey = (namespace: ConfigNamespace) =>
  `config-${namespace}`;

export const ConfigProvenanceSchema = z
  .object({
    version: z.string().min(1),
    localOverrides: z.boolean(),
    runOverride: z.boolean(),
  })
  .strict();
export type ConfigProvenance = z.infer<typeof ConfigProvenanceSchema>;
